import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import LeadStore from '../../store/LeadStore';
import { userHandler } from '../../handlers/userHandler';
import UserStore from '../../store/UserStore';

export const UsersList = observer(({ handleSelectedRowChange, toggleUserDetails }) => {
  const columns = useMemo(() => [
    { accessorKey: 'id', header: 'Id', size: 50 },
    { accessorKey: 'userName', header: 'User Name', size: 50 },
    { accessorKey: 'firstName', header: 'First Name', size: 50 },
    { accessorKey: 'lastName', header: 'Last Name', size: 50 },
    { accessorKey: 'emailId', header: 'Email', size: 50 },
    { accessorKey: 'contactNo', header: 'Phone Number', size: 50 },
    { accessorKey: 'roleCode', header: 'Role Code', size: 50 },
    { accessorKey: 'address', header: 'Address', size: 50 },
    { accessorKey: 'expires', header: 'Token Expiry', size: 50 },
    { accessorKey: 'createdBy', header: 'Created By', size: 50 },
    { accessorKey: 'updatedBy', header: 'Updated By', size: 50 },
  ]);

  const [userId, setUserId] = useState(2);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([{ id: 'userName', desc: false }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 30,
  });
  const response = useQuery({
    queryKey: ['usersList', columnFilters, globalFilter, pagination, sorting],
    queryFn: async () => {
      const sortingOrder = sorting?.[0]?.desc ? 'desc' : 'asc';
      const sortColumn = sorting?.[0]?.id;
      const res = await userHandler.fetchUsers(
        globalFilter ?? '',
        sortColumn,
        sortingOrder,
        pagination.pageIndex,
        pagination.pageSize
      );
      return res.data;
    },
    placeholderData: keepPreviousData,
  });

  const formatData = (res) => {
    return res ?? [];
  };

  const table = useMaterialReactTable({
    columns,
    data: formatData(response?.data?.items) ?? [],
    initialState: {
      showColumnFilters: false,
      columnVisibility: {},
      density: 'compact', // values can be 'spacious', 'comfortable', 'compact'
    },
    enableColumnOrdering: false,
    enableColumnResizing: false,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableStickyHeader: true,
    enableHiding: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    // positionPagination: 'top',
    muiPaginationProps: {
      rowsPerPageOptions: [30, 50, 100],
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: `${window.innerHeight - 257}px`,
        height: `${window.innerHeight - 257}px`,
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: '#cce6e6',
        '& .MuiTableSortLabel-icon': {
          color: '#F07B0E !important',
        },
        '& .MuiCheckbox-root svg': {
          color: '#F07B0E !important',
        },
      },
    },
    muiTablePaperProps: ({ table }) => ({
      //not sx
      style: {
        margin: table.getState().isFullScreen ? '95px 0px 0px 0px' : '0px',
      },
    }),
    muiToolbarAlertBannerProps: response.isError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    paginationDisplayMode: 'pages',
    getRowId: (row) => row.id,
    enableRowSelection: true,
    autoResetSelectedRows: true,
    //clicking anywhere on the row will select it
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Show individual lead details on click of row
        toggleUserDetails(row);
      },
      selected: rowSelection[row.id],
      sx: {
        cursor: 'pointer',
      },
    }),
    onRowSelectionChange: setRowSelection,
    rowCount: response?.data?.totalCount ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: response.isLoading,
      pagination,
      showAlertBanner: response.isError,
      showProgressBars: response.isFetching,
      sorting,
      rowSelection,
    },
  });

  useEffect(() => {
    response.refetch();
  }, [UserStore.refreshUsersBoard]);

  useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      handleSelectedRowChange([]);
    } else {
      handleSelectedRowChange(Object.keys(rowSelection));
    }
  }, [rowSelection]);

  return <MaterialReactTable table={table} />;
});
